import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingSettingsApi } from '@/lib/api/billing-settings';
import { Loading } from '@/components/Loading';
import { toast } from 'sonner';
import type { UpdateAccountBillingSettingsDto } from '@paladin-monorepo/common';
import { BillingSettingsForm } from '../account/BillingSettingsForm';

export function BillingSettingsTab() {
  const { t } = useTranslation();
  const { user } = useAuth({ middleware: 'auth', tmp: 'BillingSettingsTab' });
  const queryClient = useQueryClient();
  const accountId = user?.currentAccountId;

  const { data, isLoading } = useQuery({
    queryKey: ['billing-settings', accountId],
    enabled: !!accountId,
    queryFn: () => billingSettingsApi.getByAccountId(accountId!),
  });

  const mutation = useMutation({
    mutationFn: (dto: UpdateAccountBillingSettingsDto) =>
      billingSettingsApi.update(accountId!, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-settings', accountId] });
      toast.success(t('billing.saveSuccess'));
    },
    onError: () => {
      toast.error(t('billing.saveError'));
    },
  });

  if (isLoading || !data) {
    return <Loading />;
  }

  return (
    <BillingSettingsForm
      data={data}
      onSubmit={(values) => mutation.mutate(values)}
      isPending={mutation.isPending}
    />
  );
}
